import axios from 'axios';
import { TicketDetail, TicketHistory, TicketDetailResponse, WarrantyHistoryApiResponse } from '../types/ticket';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ticket-api.wetec.com.vn';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Request interceptor to add token from URL params
api.interceptors.request.use((config) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('t') || urlParams.get('token'); // Support both 't' and 'token'
    
    if (token && token.trim()) {
      config.headers.token = token.trim();
    }
  } catch (error) {
    console.error('Error extracting token from URL:', error);
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const apiService = {
  async getTicketDetail(ticketId: string, token?: string | null): Promise<TicketDetailResponse> {
    const config: any = {};
    if (token) {
      config.headers = { token };
    }
    
    // Use v1 API endpoint
    const response = await api.get(`/api/v1/tickets/${ticketId}/detail`, config);
    return response.data.data; // Access the data property from the response
  },

  async getTicketHistories(ticketId: string, token?: string | null): Promise<TicketHistory[]> {
    const config: any = {};
    if (token) {
      config.headers = { token };
    }
    
    // Use v1 API endpoint for histories
    const response = await api.get(`/api/v1/tickets/histories?ticketId=${ticketId}`, config);
    return response.data.data || response.data; // Handle both response formats
  },

  async getWarrantyHistory(serialNumber: string, page: number = 1, size: number = 10, token?: string | null): Promise<WarrantyHistoryApiResponse> {
    const config: any = {};
    if (token) {
      config.headers = { token };
    }
    
    // Use v1 API endpoint for warranty history
    const response = await api.get(`/api/v1/tickets/histories?serialNumber=${serialNumber}&page=${page}&size=${size}`, config);
    return response.data;
  },

  async acceptTicket(ticketId: string, token?: string | null): Promise<any> {
    const config: any = {};
    if (token) {
      config.headers = { token };
    }
    
    const requestBody = { token };
    
    // Use v1 API endpoint
    const response = await api.post(`/api/v1/tickets/${ticketId}/accept`, requestBody, config);
    return response.data;
  },

  async rejectTicket(ticketId: string, token?: string | null, note?: string): Promise<any> {
    const config: any = {};
    if (token) {
      config.headers = { token };
    }
    
    const requestBody = { token, historyAttachment: { note: note || '' } };
    
    // Use v1 API endpoint for reject-process
    const response = await api.post(`/api/v1/tickets/${ticketId}/reject-process`, requestBody, config);
    return response.data;
  },

  async getFileStream(fileId: string, token?: string | null): Promise<string> {
    const config: any = { responseType: 'blob' };
    if (token) {
      config.headers = { token };
    }
    
    // Use the updated files endpoint
    const response = await api.get(`/api/v1/files/${fileId}/stream`, config);
    
    // Convert blob to data URL instead of blob URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(response.data);
    });
  },

  async downloadFile(fileId: string, fileName: string, token?: string | null): Promise<void> {
    const config: any = { responseType: 'blob' };
    if (token) {
      config.headers = { token };
    }
    
    // Use the updated files endpoint for download too
    const response = await api.get(`/api/v1/files/${fileId}/download`, config);
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};